import { Tooltip as BaseTooltip } from '@base-ui/react'
import clsx from 'clsx'
import type { ReactNode } from 'react'

function TooltipArrow() {
  return (
    <svg width="16" height="8" viewBox="0 0 16 8" fill="none" aria-hidden="true">
      <path
        d="M7.29289 0.292893C7.68342 -0.0976311 8.31658 -0.0976311 8.70711 0.292893L15.7071 7.29289C16.0976 7.68342 15.8047 8.5 15.2929 8.5H0.707107C0.195262 8.5 -0.0976311 7.68342 0.292893 7.29289L7.29289 0.292893Z"
        className="fill-gray-900"
      />
    </svg>
  )
}

export interface TooltipProps {
  /** Simple string label. Use `content` instead when rich markup is needed. */
  text?: string
  /** Rich ReactNode content — use when the tooltip needs more than plain text. */
  content?: ReactNode
  /** Required unless `render` is supplied — see `render`'s JSDoc. */
  children?: ReactNode
  placement?: 'top' | 'right' | 'bottom' | 'left'
  /**
   * Cross-axis alignment relative to the trigger. `'center'` (default)
   * centers the tooltip along the trigger; `'start'` anchors it to the
   * trigger's top edge (for `placement="left"`/`"right"`) or left edge (for
   * `placement="top"`/`"bottom"`). Useful when the trigger overlaps other
   * elements and a centered tooltip could appear to point at the wrong one.
   */
  align?: 'start' | 'center' | 'end'
  /**
   * Passed through to Base UI's Positioner. By default Base UI may flip
   * `align` (`'start'` ↔ `'end'`) to avoid viewport collisions, which can
   * make an explicitly-chosen `align` appear inconsistent across triggers.
   * Set `{ align: 'none' }` to keep the requested `align` fixed.
   */
  collisionAvoidance?: BaseTooltip.Positioner.Props['collisionAvoidance']
  className?: string
  delay?: number
  /**
   * Renders the trigger as this element instead of Base UI's default
   * `<button>`. Use this when the trigger must *be* an existing interactive
   * element (e.g. another `<button>`) — wrapping that element as `children`
   * would otherwise nest buttons and produce invalid HTML. When `render` is
   * provided, Base UI merges the trigger's props onto that element and
   * `children` is ignored.
   */
  render?: BaseTooltip.Trigger.Props['render']
}

export function Tooltip({
  text,
  content,
  children,
  placement = 'top',
  align = 'center',
  collisionAvoidance,
  className,
  delay = undefined,
  render,
}: TooltipProps) {
  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger delay={delay} render={render}>
        {children}
      </BaseTooltip.Trigger>
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={placement}
          align={align}
          collisionAvoidance={collisionAvoidance}
          sideOffset={8}
          className="z-[60]"
        >
          <BaseTooltip.Arrow className="flex data-[side=top]:-bottom-2 data-[side=top]:rotate-180 data-[side=bottom]:-top-2 data-[side=bottom]:rotate-0 data-[side=left]:-right-3 data-[side=left]:rotate-90 data-[side=right]:-left-3 data-[side=right]:-rotate-90">
            <TooltipArrow />
          </BaseTooltip.Arrow>
          <BaseTooltip.Popup
            role="tooltip"
            className={clsx(
              'px-3 py-2 text-sm font-medium text-white',
              'bg-gray-900 rounded-lg shadow-sm',
              'break-words max-w-xs',
              className
            )}
          >
            {content ?? text}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
